import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState } from 'react'
import { useCreateLook } from '@/hooks/api/useLooks'
import { useAddLookToCollection } from '@/hooks/api/useCollections'
import { useCollectionEditStore } from '@/stores/collectionEdit'
import { useImageUpload } from '@/hooks/useImageUpload'
import { ImageUploadType } from '@wtf/shared'

// Global UI Components
import { Button } from '@/components/shared/atoms/Button'
import Textarea from '@/components/shared/molecules/CustomTextarea'

// Global Icons
import ChecklistIcon from '@/icons/checklist'
import PencilIcon from '@/icons/pencil'
import ShareIcon from '@/icons/share'

// Look-specific components
import { LookHeader } from '@/components/looks/LookHeader'
import { ChooseFileBottomSheet } from '@/components/shared/bottom-sheets/ChooseFileBottomSheet'
import { ImageCropperBottomSheet } from '@/components/shared/bottom-sheets/ImageCropperBottomSheet'
import { SelectNewOrExistingProductBottomSheet } from '@/components/create/product/SelectNewOrExistingProductBottomSheet'
import { CreateProductFlow } from '@/components/create/product/CreateProductFlow'
import { SelectExistingProductsBottomSheet } from '@/components/products/bottom-sheets/SelectExistingProductsBottomSheet'
import { AddLookToCollectionBottomSheet } from '@/components/looks/bottom-sheets/AddLookToCollectionBottomSheet'
import { ProductGridCard } from '@/components/products/cards/ProductGridCard/ProductGridCard'
import { AddProductPlaceholder } from '@/components/products/placeholders/AddProductPlaceholder'

export const Route = createFileRoute('/looks/create')({
    validateSearch: (search) => ({
        collectionId:
            typeof search.collectionId === 'string'
                ? search.collectionId
                : undefined,
        from: typeof search.from === 'string' ? search.from : undefined,
    }),
    component: CreateLookRoute,
})

function CreateLookRoute() {
    return <CreateLookPage />
}

function CreateLookPage() {
    const { collectionId, from } = useSearch({ from: '/looks/create' })
    const navigate = useNavigate()
    const addLookToCollection = useAddLookToCollection()
    const createLook = useCreateLook()

    // Store integration for edit mode - if adding directly to collection
    const { addLook } = useCollectionEditStore()

    // Cover upload with enhanced hook - replaces 50+ lines with 4 lines!
    const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null)
    const coverUpload = useImageUpload({
        type: ImageUploadType.LOOK_COVER,
        onSuccess: (url) => setCoverImageUrl(url),
        onError: (error) => console.error('Look cover upload failed:', error),
    })

    // Local form state
    const [description, setDescription] = useState('')
    const [productIds, setProductIds] = useState<string[]>([])

    // Bottom-sheet states
    const [showSelectNewOrExistingProduct, setShowSelectNewOrExistingProduct] =
        useState(false)
    const [showSelectExistingProducts, setShowSelectExistingProducts] =
        useState(false)
    const [createNewProductFlowOpen, setCreateNewProductFlowOpen] =
        useState(false)
    const [addLookToCollectionOpen, setAddLookToCollectionOpen] =
        useState(false)
    const [createdLookId, setCreatedLookId] = useState<string | null>(null)

    // Track create mutation loading state
    const isCreating = createLook.isPending

    const handleImageUploadProduct = () => {
        setShowSelectNewOrExistingProduct(true)
    }

    const handleRemoveProduct = (productId: string) => {
        setProductIds((prev) => prev.filter((id) => id !== productId))
    }

    const handleExistingProductsSelected = (
        selected: string[],
        removed: string[] = []
    ) => {
        setProductIds((prev) => {
            const kept = prev.filter((id) => !removed.includes(id))
            const merged = Array.from(new Set([...kept, ...selected]))
            return merged
        })
    }

    // Handle transiently-created products in look-create (not persisted until final submit)
    const handleNewProductCreated = (newId: string) => {
        setProductIds((prev) => [...prev, newId])
        setCreateNewProductFlowOpen(false)
    }

    // Handler to just close the AddLookToCollectionBottomSheet without navigation
    const handleAddToCollectionClose = () => {
        setAddLookToCollectionOpen(false)
    }

    /**
     * Handler fired after the AddLookToCollectionBottomSheet closes:
     * - If the user came from a specific collection, navigate back to that collection's edit page
     * - Otherwise, navigate to the newly created look's detail page
     */
    const handleAddToCollectionComplete = () => {
        if (collectionId) {
            // Navigate back to where the user came from
            const returnPath =
                from === 'edit'
                    ? `/collections/${collectionId}/edit`
                    : `/collections/${collectionId}`
            navigate({ to: returnPath, replace: true })
        } else if (createdLookId) {
            navigate({ to: `/looks/${createdLookId}`, replace: true })
        }
    }

    // Submit handler: create look with uploaded cover (if any)
    const handleSubmit = async () => {
        try {
            // Create the look with all data (cover already uploaded via hook if provided)
            const createdLook = await createLook.mutateAsync({
                description,
                imageUrl: coverImageUrl || undefined,
                products: productIds.map((pid) => ({ productId: pid })),
            })

            if (collectionId) {
                await addLookToCollection.mutateAsync({
                    collectionId,
                    lookId: createdLook.id,
                })

                // Update the store if coming from edit mode
                if (from === 'edit') {
                    addLook(collectionId, createdLook.id)
                }

                // Navigate back to where the user came from
                const returnPath =
                    from === 'edit'
                        ? `/collections/${collectionId}/edit`
                        : `/collections/${collectionId}`
                navigate({ to: returnPath, replace: true })
                return
            }

            setCreatedLookId(createdLook.id)
            setAddLookToCollectionOpen(true)
        } catch (error) {
            console.error('createLook flow error', error)
        }
    }

    return (
        <>
            <div className="flex h-dvh w-full max-w-lg flex-col overflow-y-auto bg-white pt-1 lg:mx-auto">
                <LookHeader
                    // showCloseIcon={false}
                    showActionLeft={true}
                    showActionRight={false}
                    image={coverUpload.previewUrl || ''}
                    showProducts={false}
                    onReport={coverUpload.triggerUpload}
                />

                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        handleSubmit()
                    }}
                >
                    <section className="mt-4 border-b border-[#f0f0f0] px-2 pb-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={coverUpload.triggerUpload}
                            className="flex w-full items-center justify-center gap-2 border-[1.5px] border-black py-3.5 text-sm font-medium"
                        >
                            {coverUpload.previewUrl ? (
                                <>
                                    <PencilIcon
                                        stroke="black"
                                        className="text-xl"
                                    />
                                    Edit Cover
                                </>
                            ) : (
                                <>
                                    <ShareIcon width={20} height={20} />
                                    Upload cover
                                </>
                            )}
                        </Button>

                        {/* Cover upload error message */}
                        {coverUpload.uploadError && (
                            <div className="mx-2 mt-2 flex items-center rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                                <img
                                    src={assetPath('icons/status/error.svg')}
                                    alt="Error"
                                    className="mr-2 h-4 w-4"
                                />
                                <span className="text-sm text-red-600">
                                    {coverUpload.uploadError}
                                </span>
                            </div>
                        )}

                        {/* Description Field */}
                        <div className="mt-4">
                            <Textarea
                                showCounter={false}
                                label="Description"
                                containerClassName=""
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                    </section>

                    {/* Products Grid */}
                    <section className="mt-4 mb-24 px-2">
                        <div className="grid grid-cols-2 gap-x-2.5 gap-y-2">
                            <AddProductPlaceholder
                                onClick={handleImageUploadProduct}
                            />
                            {productIds.map((pid) => (
                                <ProductGridCard
                                    key={pid}
                                    editMode
                                    productId={pid}
                                    onRemove={() => handleRemoveProduct(pid)}
                                />
                            ))}
                        </div>
                    </section>

                    {/* Create Button */}
                    <div className="fixed bottom-0 mx-auto w-full max-w-md bg-white/95 px-2 py-3.5 lg:max-w-lg">
                        <Button
                            type="submit"
                            fullWidth
                            className="flex w-full items-center justify-center gap-2 text-sm font-medium"
                            disabled={isCreating || coverUpload.isUploading}
                        >
                            <ChecklistIcon className="text-xl" stroke="white" />
                            {isCreating ? 'Creating...' : 'Create Look'}
                        </Button>
                        {createLook.isError && (
                            <div className="mt-2 text-center text-sm text-red-500">
                                {createLook.error?.message ||
                                    'Failed to create look. Please try again.'}
                            </div>
                        )}
                    </div>
                </form>
            </div>

            {/* Bottom Sheets */}
            <section className="fixed">
                <ChooseFileBottomSheet {...coverUpload.filePickerProps} />
                <ImageCropperBottomSheet {...coverUpload.cropperProps} />
                <SelectNewOrExistingProductBottomSheet
                    open={showSelectNewOrExistingProduct}
                    onClose={() => setShowSelectNewOrExistingProduct(false)}
                    onMyProductSelect={() => {
                        setShowSelectExistingProducts(true)
                        setShowSelectNewOrExistingProduct(false)
                    }}
                    onNewProductSelect={() => {
                        setCreateNewProductFlowOpen(true)
                        setShowSelectNewOrExistingProduct(false)
                    }}
                />
                {createNewProductFlowOpen && (
                    <CreateProductFlow
                        onClose={() => setCreateNewProductFlowOpen(false)}
                        onProductCreated={handleNewProductCreated}
                        hideAddToCollection
                    />
                )}
                <SelectExistingProductsBottomSheet
                    open={showSelectExistingProducts}
                    onClose={() => setShowSelectExistingProducts(false)}
                    onProductsSelected={handleExistingProductsSelected}
                    initialSelectedProducts={productIds}
                />
                {addLookToCollectionOpen && createdLookId && (
                    <AddLookToCollectionBottomSheet
                        open={addLookToCollectionOpen}
                        lookId={createdLookId}
                        onClose={handleAddToCollectionClose}
                        onNewLookClose={handleAddToCollectionComplete}
                    />
                )}
            </section>
        </>
    )
}
